import { useTransition } from "@vanyapr/transition-hook";
import FocusLock from "react-focus-lock";
import useOutsideClick from "../src/useOutsideClick";
import { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { HiXCircle } from "react-icons/hi";
import { OSO_LIGHT_BLUE } from "../src/colors";

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  wide?: boolean;
  extraWide?: boolean;
  tall?: boolean;
  ignoreOutsideClick?: boolean;
  showCloseButton?: boolean;
  blue?: boolean;
}

export const Portal: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [node, setNode] = useState<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = document.createElement("div");
    document.body.appendChild(node);
    setNode(node);
    return () => {
      document.body.removeChild(node);
    };
  }, [setNode]);

  if (!node) {
    return null;
  }

  return ReactDOM.createPortal(children, node);
};

const Modal: React.FC<React.PropsWithChildren<ModalProps>> = ({
  isOpen,
  onClose,
  wide,
  extraWide,
  tall,
  ignoreOutsideClick,
  children,
  showCloseButton,
  blue,
}) => {
  const { stage, shouldMount } = useTransition(isOpen, 300);
  const containerClass = stage === "enter" ? "opacity-1" : "opacity-0";
  const boxClass = stage === "enter" ? "" : "scale-95 translate-y-2";
  const outsideClickRef = useOutsideClick<HTMLDivElement>(onClose);

  useEffect(() => {
    const escapeKeyListener = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", escapeKeyListener);
    return () => document.removeEventListener("keydown", escapeKeyListener);
  }, [onClose]);

  let width = "600px";
  if (wide) width = "800px";
  if (extraWide) width = "75vw";

  return (
    <Portal>
      {shouldMount && (
        <FocusLock>
          <div
            className={`transition-opacity fixed inset-0 flex-col flex items-center z-20 bg-gray-300 dark:bg-black/60 bg-opacity-80 overflow-auto ${containerClass}`}
          >
            {!tall && <div className="mt-24"></div>}
            <div
              className={`transition-transform w-full p-4 ${boxClass}`}
              style={{ maxWidth: width }}
            >
              <div
                ref={ignoreOutsideClick ? undefined : outsideClickRef}
                className={`p-6 rounded-lg shadow-lg bg-card ${
                  tall ? "h-75vh" : ""
                }`}
                style={blue ? { backgroundColor: OSO_LIGHT_BLUE } : undefined}
              >
                {showCloseButton && (
                  <div className="relative">
                    <button
                      type="button"
                      title="Close"
                      onClick={onClose}
                      className="absolute -top-2 -right-2 text-2xl transition-colors hover:text-error"
                    >
                      <HiXCircle />
                    </button>
                  </div>
                )}
                {children}
              </div>
            </div>
          </div>
        </FocusLock>
      )}
    </Portal>
  );
};

export default Modal;
